import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from '../types/application'
import type {
  PropertyCoResidentInput,
  RawApplicationCoResident,
  PropertyTabFormData,
  PropertyApplicationContext,
  CoResidentValidatedField,
  PropertyTabInlineField,
  PropertyTabValidationResult,
  CoResidentApiPayload,
} from '../types/utils'
import { validateRentAmountForSubmit } from './rentAmountValidators'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/





/** Co-resident fields that can show per-row inline errors on the Property tab. */

/** Fields that can show inline messages on the admin Property tab. */


/** True when Angular `reasonValidate()` has activated the Reason control (admin edit). */
export function isRentLeaseReasonActiveInFormData(
  formData: Record<string, unknown>,
): boolean {
  const flag = formData.rent_lease_reason_active
  return flag === true || flag === "1" || flag === 1
}

export function isRentLeaseReasonRequired(
  context: PropertyApplicationContext,
): boolean {
  return Boolean(
    context.requireRentLeaseReason ?? context.hasRentOrLeaseChanged,
  )
}

function appendFieldError(
  fieldErrors: Partial<Record<PropertyTabInlineField, string>>,
  field: PropertyTabInlineField,
  message: string,
) {
  const prev = fieldErrors[field]
  fieldErrors[field] = prev ? `${prev}\n${message}` : message
}

const trim = (value: string | number | undefined | null): string =>
  value === undefined || value === null ? "" : String(value).trim()

const normalizePhoneDigits = (value: string | undefined | null): string =>
  trim(value).replace(/\D/g, "")

const isTruthyFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "1"

const coResidentHasEditableData = (resident: PropertyCoResidentInput): boolean =>
  [
    resident.first_name,
    resident.middle_name,
    resident.last_name,
    resident.invite_code,
    resident.applicant_invite_code,
    resident.fk_user_id,
    resident.mobile_no,
    resident.phone,
    resident.email,
    resident.spouse === true || resident.spouse === 1 || resident.spouse === "1"
      ? "1"
      : "",
    resident.relationship,
    resident.relationship_type,
  ].some((value) => trim(value as string | number | undefined | null) !== "")

const mapApplicationCoResidentToInput = (
  resident: RawApplicationCoResident,
): PropertyCoResidentInput => {
  const user = resident.user_data ?? {}

  return {
    first_name: trim(
      (resident.first_name as string | number | undefined | null) ??
        (user.first_name as string | number | undefined | null),
    ),
    middle_name: trim(
      (resident.middle_name as string | number | undefined | null) ??
        (user.middle_name as string | number | undefined | null),
    ),
    last_name: trim(
      (resident.last_name as string | number | undefined | null) ??
        (user.last_name as string | number | undefined | null),
    ),
    invite_code: trim(
      (resident.invite_code as string | number | undefined | null) ??
        (resident.applicant_invite_code as string | number | undefined | null) ??
        (user.invite_code as string | number | undefined | null) ??
        (user.applicant_invite_code as string | number | undefined | null),
    ),
    fk_user_id: trim(
      (resident.fk_user_id as string | number | undefined | null) ??
        (user.fk_user_id as string | number | undefined | null),
    ),
    mobile_no: trim(
      (resident.mobile_no as string | number | undefined | null) ??
        (resident.phone as string | number | undefined | null) ??
        (user.mobile_no as string | number | undefined | null) ??
        (user.phone as string | number | undefined | null),
    ),
    email: trim(
      (resident.email as string | number | undefined | null) ??
        (user.email as string | number | undefined | null),
    ).toLowerCase(),
    relationship_type: trim(
      (resident.relationship_type as string | number | undefined | null) ??
        (resident.relationship as string | number | undefined | null) ??
        (user.relationship_type as string | number | undefined | null) ??
        (user.relationship as string | number | undefined | null),
    ),
    spouse:
      resident.spouse === true ||
      resident.spouse === 1 ||
      resident.spouse === "1",
    applicant_status: trim(
      (resident.applicant_status as string | number | undefined | null) ??
        (user.applicant_status as string | number | undefined | null),
    ),
  }
}

export function getPropertyCoResidentInputs(
  allData: ApplicationDetailData | null | undefined,
): PropertyCoResidentInput[] {
  const directRows = allData?.co_resident ?? allData?.co_residents
  const directList = Array.isArray(directRows)
    ? directRows
    : directRows && typeof directRows === "object"
      ? Object.values(directRows)
      : []

  if (directList.length > 0) {
    return directList
      .map((resident) =>
        mapApplicationCoResidentToInput(resident as RawApplicationCoResident),
      )
      .filter(coResidentHasEditableData)
  }

  const applicationRows = allData?.co_resident_application
  const applicationList =
    applicationRows && typeof applicationRows === "object"
      ? Object.values(applicationRows as Record<string, unknown>)
      : []

  return applicationList
    .filter((resident) => {
      const row = resident as RawApplicationCoResident
      return !isTruthyFlag(row.is_primary)
    })
    .map((resident) =>
      mapApplicationCoResidentToInput(resident as RawApplicationCoResident),
    )
    .filter(coResidentHasEditableData)
}

// Normalize an applicant id for comparison (coerce number/string, strip leading zeros),
// mirroring normalizeApplicantId used for co_resident_application matching elsewhere.
const normalizeApplicantIdForMatch = (
  value: string | number | null | undefined,
): string => {
  const raw = String(value ?? "").trim()
  if (!/^\d+$/.test(raw)) return raw
  return raw.replace(/^0+/, "") || "0"
}

// Co-residents for a viewed applicant = everyone on the lease except themselves
// (Angular read-only coresidentList for a non-primary: primary + other co-applicants).
export function getCoResidentInputsExcludingApplicant(
  allData: ApplicationDetailData | null | undefined,
  selfApplicantId: string | number | null | undefined,
): PropertyCoResidentInput[] {
  const map = allData?.co_resident_application
  if (!map || typeof map !== "object") return []
  const selfId = normalizeApplicantIdForMatch(selfApplicantId)
  return Object.values(map as Record<string, unknown>)
    .filter((entry) => {
      const id = normalizeApplicantIdForMatch(
        (entry as { applicant_id?: string | number })?.applicant_id,
      )
      return id !== "" && id !== selfId
    })
    .map((entry) => mapApplicationCoResidentToInput(entry as RawApplicationCoResident))
    .filter(coResidentHasEditableData)
}

export function resolvePropertyOtherIndividualsFlag(
  formValue: unknown,
  appData: ApplicationLayerData | null | undefined,
  allData: ApplicationDetailData | null | undefined,
): boolean {
  if (formValue !== undefined && formValue !== null) {
    return isTruthyFlag(formValue)
  }

  // Angular reads the saved flag directly (`other_individuals === '1'`); never derive it.
  const savedValue = appData?.other_individuals ?? allData?.other_individuals
  return isTruthyFlag(savedValue)
}

/**
 * Angular `mobileValidator` (`customvalidation.ts`): digits-only value must match this pattern.
 */
export function isValidCoResidentPhoneDigits(digits: string): boolean {
  if (!digits) return false
  return /^([+]?\d{1,2}[.-\s]?)?(\d{3})(\d{3})(\d{4})$/.test(digits)
}

const isRentValuePresent = (value: string | number | undefined | null): boolean => {
  if (value === 0 || value === "0" || value === "0.00") return true
  return trim(value) !== ""
}

/** Merge form delta + saved application for leasing-agent validation on save/tab switch. */
export function resolvePropertyAgentFields(
  formDelta: Partial<PropertyTabFormData>,
  allData: ApplicationDetailData | null | undefined,
): Pick<
  PropertyTabFormData,
  "agent_id" | "agent_name" | "agent_email" | "agent_phone"
> {
  const agent = (allData?.agent ?? {}) as {
    user_id?: string | number
    agent_id?: string | number
    email?: string
    agent_email?: string
    first_name?: string
    last_name?: string
    agent_name?: string
    mobile_no?: string
    agent_mobile_no?: string
  }
  const other = (allData?.other_agent ?? {}) as {
    agent_name?: string
    agent_email?: string
    agent_mobile_no?: string
  }

  let agentId =
    formDelta.agent_id !== undefined ? trim(formDelta.agent_id) : ""
  if (!agentId) {
    const realId = allData?.fk_agent_id ?? agent?.user_id ?? agent?.agent_id
    if (realId != null && String(realId).trim() !== "") {
      agentId = String(realId)
    } else if (other?.agent_name || other?.agent_email || other?.agent_mobile_no) {
      agentId = "Other"
    }
  }

  const agentName =
    formDelta.agent_name !== undefined
      ? trim(formDelta.agent_name)
      : agentId === "Other"
        ? trim(other?.agent_name)
        : [agent?.first_name, agent?.last_name].filter(Boolean).join(" ") ||
          trim(agent?.agent_name)

  const agentEmail =
    formDelta.agent_email !== undefined
      ? trim(formDelta.agent_email)
      : trim(agent?.email) ||
        trim(agent?.agent_email) ||
        trim(other?.agent_email)

  const agentPhone =
    formDelta.agent_phone !== undefined
      ? trim(formDelta.agent_phone)
      : trim(agent?.mobile_no) ||
        trim(agent?.agent_mobile_no) ||
        trim(other?.agent_mobile_no)

  return {
    agent_id: agentId,
    agent_name: agentName,
    agent_email: agentEmail,
    agent_phone: agentPhone,
  }
}

export function validatePropertyTab(
  data: PropertyTabFormData,
  context: PropertyApplicationContext = {},
): PropertyTabValidationResult {
  const errors: string[] = []
  const fieldErrors: Partial<Record<PropertyTabInlineField, string>> = {}
  let coResidentFieldErrors: Partial<
    Record<CoResidentValidatedField, string>
  >[] = []

  // Angular parity: UI defaults to "Yes" when the user hasn't explicitly chosen yet.
  // So undefined/null should behave like has_applied=true for validation.
  const hasApplied =
    data.has_applied === false ? false : true

  if (hasApplied === false) {
    return { isValid: true, errors, fieldErrors: {} }
  }

  if (!trim(data.property_name)) {
    const m = "Property Name is required."
    errors.push(m)
    appendFieldError(fieldErrors, "property_name", m)
  }

  if (!isRentValuePresent(data.rent_amount)) {
    const m = "Monthly Rent Amount is required."
    errors.push(m)
    appendFieldError(fieldErrors, "rent_amount", m)
  } else {
    const rentErr = validateRentAmountForSubmit(String(data.rent_amount))
    if (rentErr) {
      errors.push(rentErr)
      appendFieldError(fieldErrors, "rent_amount", rentErr)
    }
  }

  if (!trim(data.lease_start_date)) {
    const m = "Lease Start Date is required."
    errors.push(m)
    appendFieldError(fieldErrors, "lease_start_date", m)
  }

  if (isRentLeaseReasonRequired(context) && !trim(data.rent_lease_reason)) {
    const m = "'Reason' is required."
    errors.push(m)
    appendFieldError(fieldErrors, "rent_lease_reason", m)
  }

  // Angular `agentSelect`: when consultant is "Other", name/email/phone are required (+ email format).
  if (trim(data.agent_id) === "Other") {
    if (!trim(data.agent_name)) {
      const m = "Leasing Consultant/Property Manager Name is required."
      errors.push(m)
      appendFieldError(fieldErrors, "agent_name", m)
    }

    const agentEmail = trim(data.agent_email)
    if (!agentEmail) {
      const m = "Leasing Consultant/Property Manager Email is required."
      errors.push(m)
      appendFieldError(fieldErrors, "agent_email", m)
    } else if (!EMAIL_PATTERN.test(agentEmail)) {
      const m = "Enter Valid Email."
      errors.push(m)
      appendFieldError(fieldErrors, "agent_email", m)
    }

    const agentPhoneDigits = normalizePhoneDigits(data.agent_phone)
    if (!agentPhoneDigits) {
      const m = "Leasing Consultant/Property Manager Phone is required."
      errors.push(m)
      appendFieldError(fieldErrors, "agent_phone", m)
    } else if (!isValidCoResidentPhoneDigits(agentPhoneDigits)) {
      const m =
        "Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX]."
      errors.push(m)
      appendFieldError(fieldErrors, "agent_phone", m)
    }
  }

  const otherIndividuals =
    data.other_individuals === true || data.other_individuals === "1"
  const list = data.co_residents ?? []

  if (otherIndividuals) {
    if (list.length === 0) {
      const m = "At least one co-resident is required."
      errors.push(m)
    }

    const norm = (s: string) => normalizePhoneDigits(s)
    const seenEmails: string[] = []
    const seenMobiles: string[] = []

    const primaryEmail = trim(context.primaryEmail).toLowerCase()
    if (primaryEmail) seenEmails.push(primaryEmail)
    const primaryMobile = norm(context.primaryMobile ?? "")
    if (primaryMobile) seenMobiles.push(primaryMobile)

    coResidentFieldErrors = list.map(() => ({}))

    const rowSet = (
      idx: number,
      field: CoResidentValidatedField,
      message: string,
    ) => {
      const row = coResidentFieldErrors[idx]!
      if (!row[field]) row[field] = message
    }

    list.forEach((resident, idx) => {
      const label = `Co-Applicant ${idx + 1}`
      const firstName = trim(resident.first_name)
      const lastName = trim(resident.last_name)
      if (!firstName) {
        const m = `${label}: 'First Name' is required.`
        errors.push(m)
        rowSet(idx, "first_name", "'First Name' is required.")
      }
      if (!lastName) {
        const m = `${label}: 'Last Name' is required.`
        errors.push(m)
        rowSet(idx, "last_name", "'Last Name' is required.")
      }

      const email = trim(resident.email)
      const mobileRaw = trim(resident.mobile_no ?? resident.phone)
      const mobileDigits = norm(mobileRaw)
      const relationship = trim(
        resident.relationship_type ?? resident.relationship,
      )

      if (!relationship) {
        const m = `${label}: Relationship is required.`
        errors.push(m)
        rowSet(idx, "relationship", "'Relationship' is required.")
      }

      if (!mobileDigits) {
        const m = `${label}: Phone No is required.`
        errors.push(m)
        rowSet(idx, "phone", "'Phone No' is required.")
      } else if (!isValidCoResidentPhoneDigits(mobileDigits)) {
        const m = `${label}: Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].`
        errors.push(m)
        rowSet(
          idx,
          "phone",
          "Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].",
        )
      }

      if (email && !EMAIL_PATTERN.test(email)) {
        const m = `${label}: Email — Enter Valid Email.`
        errors.push(m)
        rowSet(idx, "email", "Email is not valid.")
      }

      if (email) {
        const lower = email.toLowerCase()
        if (seenEmails.includes(lower)) {
          const m = `${label}: Email should be different from Primary Applicant or Other Co-Applicant.`
          errors.push(m)
          rowSet(
            idx,
            "email",
            "Email should be different from Primary Applicant or Other Co-Applicant.",
          )
        } else {
          seenEmails.push(lower)
        }
      }

      if (mobileDigits) {
        if (seenMobiles.includes(mobileDigits)) {
          const m = `${label}: Mobile Number should be different from Primary Applicant or Other Co-Applicant.`
          errors.push(m)
          rowSet(
            idx,
            "phone",
            "Mobile Number should be different from Primary Applicant or Other Co-Applicant.",
          )
        } else {
          seenMobiles.push(mobileDigits)
        }
      }
    })
  }

  const hasRowErrors = coResidentFieldErrors.some((row) =>
    Object.keys(row).length > 0,
  )

  return {
    isValid: errors.length === 0 && !hasRowErrors,
    errors,
    fieldErrors,
    ...(hasRowErrors ? { coResidentFieldErrors } : {}),
  }
}


export function mapCoResidentToApi(
  resident: PropertyCoResidentInput,
): CoResidentApiPayload {
  const out: CoResidentApiPayload = {}

  const hasFkUserId = Object.prototype.hasOwnProperty.call(
    resident,
    "fk_user_id",
  )
  const fkUserId = trim(resident.fk_user_id)
  if (hasFkUserId || fkUserId) out.fk_user_id = fkUserId

  const first = trim(resident.first_name)
  if (first) out.first_name = first
  const middle = trim(resident.middle_name)
  if (middle) out.middle_name = middle
  const last = trim(resident.last_name)
  if (last) out.last_name = last

  const hasInviteCode = Object.prototype.hasOwnProperty.call(
    resident,
    "invite_code",
  )
  const invite = hasInviteCode
    ? trim(resident.invite_code)
    : trim(resident.applicant_invite_code)
  if (hasInviteCode || invite) out.invite_code = invite

  const mobile = normalizePhoneDigits(resident.mobile_no ?? resident.phone)
  if (mobile) out.mobile_no = mobile

  // Angular sends the email control even when blank on new co-applicant rows.
  if (
    Object.prototype.hasOwnProperty.call(resident, "email") ||
    trim(resident.email) !== ""
  ) {
    out.email = trim(resident.email).toLowerCase()
  }

  if (resident.spouse !== undefined && resident.spouse !== null) {
    out.spouse =
      resident.spouse === true ||
      resident.spouse === 1 ||
      resident.spouse === "1"
  }

  const relationship = trim(resident.relationship_type ?? resident.relationship)
  if (relationship) out.relationship_type = relationship

  return out
}

export type {
  PropertyCoResidentInput,
  PropertyTabFormData,
  PropertyApplicationContext,
  PropertyTabInlineField,
  CoResidentValidatedField,
} from '../types/utils'

